import { Fragment } from 'react'
import { twMerge } from 'tailwind-merge'

import type { SettingsSection, SettingsSectionId } from './settings-modal-types'

/** Fallback when no role-aware labeler is supplied: label the first group "Settings", none after. */
const defaultGroupLabel = (group: number): string | null => (group === 1 ? 'Settings' : null)

type SettingsNavProps = {
  /** Sections to render, in order. Items are grouped by `group` with a divider between groups. */
  sections: SettingsSection[]
  /** Currently active section. */
  activeSectionId: SettingsSectionId
  /** Fired when the user selects a section. */
  onSectionChange: (id: SettingsSectionId) => void
  /** Header label for a group (e.g. "Personal settings", the org name). `null` renders no header. */
  groupLabel?: (group: number) => string | null
}

/**
 * Left-hand navigation for the settings modal. Presentational only: it renders the section list and
 * reports selections up via `onSectionChange`. Each group gets a header label (from `groupLabel`) and
 * a horizontal divider separates adjacent groups.
 */
export function SettingsNav({ sections, activeSectionId, onSectionChange, groupLabel = defaultGroupLabel }: SettingsNavProps) {
  return (
    // `pt-6` (24px) matches the content column's top padding so the first group label lines up with the section header.
    <nav aria-label="Settings" className="flex w-50 shrink-0 flex-col gap-0.5 border-r border-border-default pb-3 pt-6">
      {sections.map((section, index) => {
        const previous = sections[index - 1]
        const startsNewGroup = previous === undefined || previous.group !== section.group
        const header = startsNewGroup ? groupLabel(section.group) : null
        const isActive = section.id === activeSectionId
        const Icon = section.icon

        return (
          <Fragment key={section.id}>
            {startsNewGroup && index > 0 && <div className="my-2 border-t border-border-default" />}
            <div className="px-2">
              {header && <h2 className="flex items-center px-2 py-2 h-8 text-xs font-semibold uppercase tracking-wide text-brand-neutral-500">{header}</h2>}
              <button
                type="button"
                aria-current={isActive ? 'page' : undefined}
                onClick={() => onSectionChange(section.id)}
                className={twMerge(
                  'flex items-center gap-2 w-full rounded-md py-2 px-2 text-left text-sm font-medium text-brand-neutral-700 transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                  isActive && 'bg-surface-selected-subtle text-text-primary'
                )}
              >
                <Icon className="size-4.5 shrink-0" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{section.label}</span>
              </button>
            </div>
          </Fragment>
        )
      })}
    </nav>
  )
}
